/**
 * @typedef {import('./index.js').Store<State, Getters>} Store
 */
/**
 * @template T
 * @typedef {import('./index.js').GetterFunction<T, State, Getters>} GetterFunction<T>
 */
/**
 * @template T
 * @template R
 * @typedef {import('./index.js').ActionFunction<T, R, State, Getters>} ActionFunction<T,R>
 */
/**
 * @typedef {Object} DataLayerUser
 * @property {string} accountNumber - User Number
 * @property {string} accountType - User loyalty level
 * @property {'visitor'|'logged in'} loginState - User Login State
 */
/**
 * @typedef {Object} Payload
 * @property {string} id
 * @property {string} token
 * @property {string} expiration
 * @property {UserProfile} profile
 * @property {string} redirect
 */
/**
 * @typedef {Object} UserProfile
 * @property {string} fanId
 * @property {string} loyalLevel
 * @property {string} title
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} birthDate
 * @property {string} signUpDate
 * @property {number} gender
 * @property {string} genderLabel
 * @property {string} languagePreferenceLabel
 * @property {string} email
 * @property {Array<PhoneModel>} phoneNumbers
 * @property {Array<AddressModel>} addresses
 * @property {string} languagePreference
 * @property {Array<{id: string, label: string}>} locations
 * @property {Array<string>} locationInterestPreferences
 * @property {Array<string>} interestedPreferences
 * @property {Array<string>} otherPreferences
 * @property {Array<string>} communicationInterest
 * @property {boolean} sensitiveDataAccepted
 * @property {boolean} newsletter
 * @property {boolean} accessibleRoom
 * @property {boolean} sustainableStay
 * @property {string} waterPreference
 * @property {Array<string>} interests
 */
/**
 * @typedef {Object} PhoneModel
 * @property {number} type
 * @property {string} [useType="Personal"]
 * @property {string} [techType = "Mobile"]
 * @property {string} countryCode
 * @property {string} number
 */
/**
 * @typedef {Object} AddressModel
 * @property {number} type
 * @property {string} country
 * @property {string} state
 * @property {string} city
 * @property {string} postalCode
 * @property {string} addressLineOne
 * @property {string} addressLineTwo
 * @property {string} addressTypeLabel
 * @property {string} companyName
 */
import axios from 'axios'
import { logger } from '@plugins/logger'
import { getApiUrl } from '@utils/api-helpers'
import transformPreferences from '@utils/transformPreferences'

export const DISMISSED_ALERTS_ID = 'dismissed-alert-ids'
/**
 * @module Store:Auth
 * @description
 * Handles all user authetication / profile calls for the application.
 */

/** Get user data from cookie.
 */
const getUserFromPage = () => {
  const cookies = document.cookie.split('; ')
  const userCookie = cookies.find((row) => row.startsWith('mo-user='))

  if (userCookie) {
    try {
      const encodedUserData = userCookie.split('=')[1]
      const userDataString = decodeURIComponent(encodedUserData)
      const userData = JSON.parse(userDataString)
      return userData
    } catch (error) {
      logger.error('Error parsing user data cookie:', error)
      document.cookie = 'mo-user=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;'
      return null
    }
  }
  return null
}

const getUserDismissedAlerts = () => {
  const localStorageIds = localStorage.getItem(DISMISSED_ALERTS_ID)
  if (localStorageIds) {
    return JSON.parse(localStorageIds)
  }

  return []
}

export default {
  namespaced: true,
  /** Auth State
   * @typedef {Object} State
   * @property {UserProfile|DataLayerUser|undefined} user - User object populated from __initialpageviewdata.user for logged in user.profile
   * @property {Object} payload - Complete payload from server exept profile
   * @property {Boolean} isLoginPending - Login api call pending state
   * @property {Boolean} isRegisterPending - Registration api call pending state
   * @property {Boolean} isGetProfilePending - Get profile api call pending state
   * @property {Boolean} isUpdateProfilePending - Update profile api call pending state
   * @property {Array} authErrors - Array of errors on auth flow
   * @property {Object} loginComponentData - Data for login component
   * @property {Object} fansJoinComponentData - Data for fans join component
   * @property {Object} registrationComponentData - Data for registration component
   * @property {Object} complimentaryPrivilegesComponentData - Data for complimentary privileges component (join-fans.vue inside booking wizard)
   * @property {Object} addOnsComponentData - Data for add-ons component (booking-add-ons.vue)
   */
  /** @type {State} */
  state: {
    user: getUserFromPage(),
    payload: null,
    isLoginPending: false,
    isRegisterPending: false,
    isGetProfilePending: false,
    isUpdateProfilePending: false,
    authErrors: [],
    loginComponentData: null,
    fansJoinComponentData: null,
    registrationComponentData: null,
    complimentaryPrivilegesComponentData: null,
    addOnsComponentData: null,
    numberOfAllowedEnhancements: 2,
    dismissedAlertIds: getUserDismissedAlerts()
  },
  /** Auth Getters
   * @typedef {Object} Getters
   * @property {GetterFunction<string|number>} userId - Logged user's ID
   * @property {GetterFunction<string>} fanLevel - Logged user's Fans Level
   * @property {GetterFunction<string>} userCurrency - Currency from user profile, not in use at this moment
   * @property {GetterFunction<boolean>} haveProfileData - Check if profile have been loaded
   * @property {GetterFunction<boolean>} isProfilePending - Get profile call pending state
   * @property {GetterFunction<number>} enhancementsAllowed - Number of enhancements allowed per room for this user
   * @property {GetterFunction<boolean>} isTravelAgent - Is the logged in user a travel agent?
   * @property {GetterFunction<Object>} travelAgentInfo - Information of the travel agent:
   * @property {GetterFunction<boolean>} haveLoginError - Are there any errors?
   * @property {GetterFunction<string|number>} travelAgentInfo.IATA - ID of the agent
   * @property {GetterFunction<string>} travelAgentInfo.agency - Name of the agency
   * @property {GetterFunction<boolean>} isAuthenticated - Is user authenticated?
   * @property {GetterFunction<function(string): string>} getLoginComponentData - Get data for login component
   * @property {GetterFunction<function(string): string>} getRegistrationComponentData - Get data for registration component
   */
  /** @type {Getters} */
  getters: {
    userId: (state) => {
      const fanId = state.user?.fanId
      const accountNumber = state.user?.accountNumber
      return fanId || accountNumber
    },
    fanLevel: (state) => {
      const userFanLevel = state.user?.accountType
      const profileFanLevel = state.user?.loyalLevel
      return userFanLevel || profileFanLevel
    },
    userCurrency: (state) => state.user?.currencyPreference,
    haveProfileData: (state) => !!state.user?.fanId,
    isProfilePending: (state) => state.isGetProfilePending || state.isUpdateProfilePending,
    enhancementsAllowed: (state, getters) => state.numberOfAllowedEnhancements,
    isTravelAgent: (state) => !!(state.user && state.user.IATA),
    travelAgentInfo: (state, getters) => (getters.isTravelAgent ? {
      IATA: state.user.IATA,
      agency: state.user.agency
    } : {}),
    haveLoginError: (state) => state.authErrors.length > 0,
    isAuthenticated: (state) => !!state.user,
    getLoginComponentData: (state) => (prop) => state.loginComponentData && state.loginComponentData[prop],
    getRegistrationComponentData: (state) => (prop) => state.registrationComponentData && state.registrationComponentData[prop],
    getDismissedIds: (state) => state.dismissedAlertIds
  },
  /**
   * Auth Mutations
   */
  mutations: {
    UPDATE_USER: (/** @type {State} */
      state, user
    ) => {
      state.user = user
    },
    PATCH_USER: (/** @type {State} */
      state, user
    ) => {
      Object.assign(state.user, user)
    },
    UPDATE_PAYLOAD: (/** @type {State} */
      state, payload
    ) => {
      const payloadClone = {
        ...payload
      }
      delete payloadClone.profile
      state.payload = payloadClone
    },
    UPDATE_AUTH_ERRORS: (/** @type {State} */
      state, error
    ) => {
      state.authErrors = []
      if (error?.response) {
        state.authErrors.push({ error: error.response.data, status: error.response.status, message: error.response.message })
        logger.error(error.response)
      } else {
        logger.error(error)
        state.authErrors.push({ status: 500, message: 'Something went wrong' })
      }
    },
    CLEAR_AUTH_ERRORS: (/** @type {State} */
      state
    ) => {
      state.authErrors = []
    },
    UPDATE_LOGIN_PENDING_STATE: (/** @type {State} */
      state, loginState
    ) => {
      state.isLoginPending = !!loginState
    },
    UPDATE_REGISTER_PENDING_STATE: (/** @type {State} */
      state, registerState
    ) => {
      state.isRegisterPending = !!registerState
    },
    UPDATE_GET_PROFILE_PENDING_STATE: (/** @type {State} */
      state, status
    ) => {
      state.isGetProfilePending = !!status
    },
    UPDATE_PROFILE_PENDING_STATE: (/** @type {State} */
      state, status
    ) => {
      state.isUpdateProfilePending = !!status
    },
    UPDATE_AUTH_COMPONENTS: (/** @type {State} */
      state, {
        login, registration, fansJoin, complimentaryPrivileges, addOnsLabels
      }
    ) => {
      if (login) {
        state.loginComponentData = login
      }
      if (registration) {
        state.registrationComponentData = registration
      }
      if (fansJoin) {
        state.fansJoinComponentData = fansJoin
      }
      if (complimentaryPrivileges) {
        state.complimentaryPrivilegesComponentData = complimentaryPrivileges
      }
      if (addOnsLabels) {
        state.addOnsComponentData = addOnsLabels
      }
    },
    UPDATE_DISMISSED_ALERT_MESSAGE: (state, id) => {
      state.dismissedAlertIds.push(id)
      localStorage.setItem(DISMISSED_ALERTS_ID, JSON.stringify(state.dismissedAlertIds))
    },
    RESET_DISMISSED_ALERT_MESSAGE: (state) => {
      state.dismissedAlertIds = []
      localStorage.setItem(DISMISSED_ALERTS_ID, JSON.stringify([]))
    }
  },
  /** Auth Actions
   * @typedef {Object} Actions
   * @property {ActionFunction<UserProfile,Payload>} register - Register a [user]{@link UserProfile}
   * @property {ActionFunction<{username: string, password: string},Payload>} login - Login a [user]{@link UserProfile}
   * @property {ActionFunction<undefined,void>} logout - Logout the logged in [user]{@link UserProfile}
   * @property {ActionFunction<undefined,void>} getProfile - Get logged in user's full [user]{@link UserProfile}
   * @property {ActionFunction<{data: UserProfile, endpoint: string, method: 'patch'},void>} updateProfile - Update logged in user's [user]{@link UserProfile}
   * @property {ActionFunction<{login: Object, registration: Object, fansJoin: Object},void>} updateAuthComponents - Update auth components data
   */
  /** @type {Actions} */
  actions: {
    async register(
      {
        commit,
        getters,
        dispatch,
      },
      userData
    ) {
      if (getters.haveProfileData) {
        throw new Error('User already logged in')
      }
      commit('UPDATE_REGISTER_PENDING_STATE', true)
      try {
        const { data: payload } = await axios.post(getApiUrl('/api/v1/profile/register'), userData)
        commit('CLEAR_AUTH_ERRORS')
        if ('profile' in payload) {
          commit('UPDATE_USER', payload.profile)
          commit('UPDATE_PAYLOAD', payload)
          dispatch('setUserCookie', payload)
        }
        commit('RESET_DISMISSED_ALERT_MESSAGE')
        commit('UPDATE_REGISTER_PENDING_STATE', false)
        return payload
      } catch (err) {
        commit('UPDATE_REGISTER_PENDING_STATE', false)
        commit('UPDATE_AUTH_ERRORS', err)
        throw err
      }
    },
    async login(
      {
        commit,
        getters,
        dispatch
      },
      { password, username, grecaptchaToken }
    ) {
      if (getters.haveProfileData) {
        throw new Error('User already logged in')
      }
      commit('UPDATE_LOGIN_PENDING_STATE', true)
      try {
        const { data: payload } = await axios.post(getApiUrl('/api/v1/accounts/login'), { password, username, grecaptchaToken })
        commit('CLEAR_AUTH_ERRORS')
        if ('profile' in payload) {
          commit('UPDATE_USER', payload.profile)
          commit('UPDATE_PAYLOAD', payload)
          dispatch('setUserCookie', payload)
        }
        commit('RESET_DISMISSED_ALERT_MESSAGE')
        commit('UPDATE_LOGIN_PENDING_STATE', false)
        return payload
      } catch (err) {
        commit('UPDATE_LOGIN_PENDING_STATE', false)
        commit('UPDATE_AUTH_ERRORS', err)
        throw err
      }
    },
    async logout({ commit, state, rootState }) {
      if (!state.user) {
        window.location.href = `/${rootState.global.lang}/fans-of-mo/login`
        return Promise.resolve()
      }
      try {
        const { data } = await axios.post(getApiUrl('/api/v1/accounts/logout'))
        window.location.href = `/${rootState.global.lang}/fans-of-mo/login`
        return data
      } catch (error) {
        logger.error('Logout API call failed:', error)
        commit('UPDATE_AUTH_ERRORS', error)
        window.location.href = `/${rootState.global.lang}/fans-of-mo/login`
        return error
      } finally {
        document.cookie = 'mo-user=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; Secure; SameSite=Lax'
        commit('UPDATE_USER', null)
        commit('UPDATE_PAYLOAD', null)
      }
    },
    async getProfile(
      { commit, state }
    ) {
      if (!state.user) {
        throw new Error('User is not logged in')
      }
      commit('UPDATE_GET_PROFILE_PENDING_STATE', true)
      try {
        const { data } = await axios.get(getApiUrl('/api/v1/profile/me'))
        commit('UPDATE_USER', data.profile)
      } catch (err) {
        commit('UPDATE_AUTH_ERRORS', err)
      }
      commit('UPDATE_GET_PROFILE_PENDING_STATE', false)
    },
    async updateProfile(
      {
        commit,
        state
      },
      {
        data: profile,
        endpoint,
        method = 'patch'
      }
    ) {
      if (!state.user) {
        throw new Error('User is not logged in')
      }
      commit('UPDATE_PROFILE_PENDING_STATE', true)

      if (endpoint === 'local-mo') {
        profile.otherPreferences = profile.otherPreferences.map((p) => {
          const _p = JSON.parse(p)
          profile.remove = _p.remove
          return _p.value
        })
      }
      console.table(profile)
      const params = method === 'delete' ? {
        data: profile
      } : profile
      try {
        console.log(params)
        const { data } = await axios[method](getApiUrl(`api/v1/profile/update/${endpoint}`), params)
        commit('UPDATE_USER', data)
      } catch (err) {
        commit('UPDATE_AUTH_ERRORS', err)
      }
      commit('UPDATE_PROFILE_PENDING_STATE', false)
    },
    updateAuthComponents({
      commit
    }, {
      login, registration, fansJoin, complimentaryPrivileges, addOnsLabels
    }) {
      commit('UPDATE_AUTH_COMPONENTS', {
        login, registration, fansJoin, complimentaryPrivileges, addOnsLabels
      })
    },
    dismissAlertMessage({ commit }, id) {
      commit('UPDATE_DISMISSED_ALERT_MESSAGE', id)
    },
    setUserCookie(_, payload) {
      if (!payload?.profile && !payload?.expiration) {
        throw new Error('Invalid payload: profile and expiration are required')
      }
      const expirationDate = new Date(payload.expiration)
      const expiresUTC = expirationDate.toUTCString()

      const userCookieData = JSON.stringify({
        loginState: 'logged in',
        accountType: payload?.profile?.loyalLevel ?? '',
        accountNumber: payload?.profile?.fanId.toLowerCase() ?? '',
        ireckonuID: String(payload?.profile?.personId ?? '')
      })

      document.cookie = `mo-user=${encodeURIComponent(userCookieData)}; expires=${expiresUTC}; path=/; Secure; SameSite=Lax`
    }
  }
}
